import React from "react";
import { Accordion, Table } from "react-bootstrap";
import moment from "moment";

/**
 * Item de acordeón para una sola acta. Se renderiza dentro de un <Accordion> del modal
 * de actas por OT (TIN-15).
 *
 * Props:
 * - acta: objeto del DTO del backend (campos: _id, consecutivo, tipo, entrega, createdAt,
 *   autor, cantidadEquipos, Equipos[]).
 * - eventKey: string para el Accordion.Item (típicamente el _id del acta).
 * - onDescargarPdf: (acta) => void — handler que recibe el objeto completo del acta.
 */
const nombreCompleto = (usuario) => {
  if (!usuario) return "";
  const nombre = usuario.nombre || "";
  const apellido = usuario.apellido || "";
  return `${nombre} ${apellido}`.trim();
};

const fechaParaResponsable = (eq) => {
  // Prioridad fechaCancelado > fechaProcesado (decisión D7 del design, Q2 confirmada)
  const fecha = eq?.fechaCancelado || eq?.fechaProcesado;
  if (!fecha) return "";
  return moment(fecha).format("DD/MM/YYYY");
};

const formatearFecha = (fecha) => (fecha ? moment(fecha).format("DD/MM/YYYY") : "");

export const ActaAccordionItem = ({ acta, eventKey, onDescargarPdf }) => {
  const equipos = Array.isArray(acta?.Equipos) ? acta.Equipos : [];

  return (
    <Accordion.Item eventKey={eventKey}>
      <Accordion.Header>
        <div className="d-flex align-items-center gap-3 w-100 flex-wrap me-2">
          <strong>#{acta.consecutivo}</strong>
          {acta.tipo && (
            <span className="badge bg-secondary text-uppercase">{acta.tipo}</span>
          )}
          {acta.entrega && (
            <span className="badge bg-info text-dark">{acta.entrega}</span>
          )}
          <span className="text-muted small">
            <i className="bi bi-calendar3 me-1" />
            {formatearFecha(acta.createdAt)}
          </span>
          <span className="small">
            <i className="bi bi-person me-1" />
            {nombreCompleto(acta.autor)}
          </span>
          <span className="badge bg-primary">
            {acta.cantidadEquipos ?? equipos.length} equipo(s)
          </span>
          {/*
            Render del botón como <span role="button"> en vez de <Button> para evitar
            HTML inválido (<button> dentro del <button> del Accordion.Header de React
            Bootstrap, que descarta stopPropagation en el navegador). Bug encontrado
            en QA E2E TIN-15.
          */}
          <span
            role="button"
            tabIndex={0}
            className="btn btn-outline-danger btn-sm ms-auto"
            onClick={(e) => {
              e.stopPropagation();
              if (onDescargarPdf) onDescargarPdf(acta);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                if (onDescargarPdf) onDescargarPdf(acta);
              }
            }}
          >
            <i className="bi bi-file-earmark-pdf me-2" />
            Descargar PDF
          </span>
        </div>
      </Accordion.Header>
      <Accordion.Body>
        {equipos.length === 0 ? (
          <p className="text-muted fst-italic mb-0">
            Sin equipos registrados en esta acta.
          </p>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="mb-0 align-middle">
              <thead className="table-secondary">
                <tr>
                  <th>Consecutivo</th>
                  <th>Ítem</th>
                  <th>Marca</th>
                  <th>Modelo</th>
                  <th>Serial</th>
                  <th>Ubicación</th>
                  <th>Estado</th>
                  <th>Responsable / Fecha</th>
                  <th>Fecha inclusión</th>
                </tr>
              </thead>
              <tbody>
                {equipos.map((eq, idx) => {
                  const equipo = eq?.equipo || {};
                  const snapshot = equipo.snapshot || {};
                  const fechaResp = fechaParaResponsable(equipo);
                  return (
                    <tr key={eq?._id || idx}>
                      <td>{equipo.consecutivo || "—"}</td>
                      <td>{snapshot.item || "—"}</td>
                      <td>{snapshot.marca || "—"}</td>
                      <td>{snapshot.modelo || "—"}</td>
                      <td>{snapshot.serial || "—"}</td>
                      <td>{snapshot.ubicacion || "—"}</td>
                      <td>{equipo.estado || "—"}</td>
                      <td>
                        <div>
                          <strong>{nombreCompleto(equipo.responsable) || "—"}</strong>
                        </div>
                        {fechaResp && (
                          <div className="text-muted small">{fechaResp}</div>
                        )}
                      </td>
                      <td>{formatearFecha(eq?.fechaIngresado) || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
};

export default ActaAccordionItem;
